import { ArrowLeft, Mail, MessageSquare, FileText, Clock, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../seo';

export default function Support() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead page={PAGE_SEO.support} />
      <Header />

      <div className="flex-1 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-pb-gray-500 hover:text-pb-orange mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-display font-bold text-pb-gray-800 mb-2">
              Support Center
            </h1>
            <p className="text-pb-gray-500">
              Get help with PartnerBuddy - we're here to assist you
            </p>
          </div>

          {/* Response Time Banner */}
          <div className="bg-pb-blue/5 border border-pb-blue/20 rounded-xl p-4 mb-8 flex items-center">
            <Clock className="w-5 h-5 text-pb-blue mr-3 flex-shrink-0" />
            <p className="text-pb-gray-700">
              <strong>Response Time:</strong> We respond to all support requests within{' '}
              <strong>2 business days</strong>
            </p>
          </div>

          {/* Contact Options */}
          <div className="grid gap-6 mb-8">
            {/* Email Support */}
            <div className="bg-white rounded-2xl shadow-rhythmic-xl p-6">
              <div className="flex items-start">
                <div className="bg-pb-orange/10 rounded-lg p-3 mr-4">
                  <Mail className="w-6 h-6 text-pb-orange" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-pb-gray-800 mb-2">
                    Email Support
                  </h2>
                  <p className="text-pb-gray-600 mb-4">
                    For general inquiries, technical support, or account questions.
                  </p>
                  <a
                    href="mailto:support@rhythmic.dev"
                    className="inline-flex items-center px-4 py-2 bg-pb-orange text-white rounded-lg font-medium hover:bg-pb-orange-dark hover:shadow-lg transition-all"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    support@rhythmic.dev
                  </a>
                </div>
              </div>
            </div>

            {/* In-App Support */}
            <div className="bg-white rounded-2xl shadow-rhythmic-xl p-6">
              <div className="flex items-start">
                <div className="bg-pb-blue/10 rounded-lg p-3 mr-4">
                  <MessageSquare className="w-6 h-6 text-pb-blue" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-pb-gray-800 mb-2">
                    In-App Help
                  </h2>
                  <p className="text-pb-gray-600 mb-4">
                    Get help directly within your messaging platform:
                  </p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-pb-gray-500 mb-1">Slack:</p>
                      <div className="bg-pb-gray-50 rounded-lg p-3 font-mono text-sm">
                        @PartnerBuddy help
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-pb-gray-500 mb-1">Microsoft Teams:</p>
                      <div className="bg-pb-gray-50 rounded-lg p-3 font-mono text-sm">
                        @PartnerBuddy help
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Common Topics */}
          <div className="bg-white rounded-2xl shadow-rhythmic-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-pb-gray-800 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none py-3 border-b border-pb-gray-100">
                  <span className="font-medium text-pb-gray-700">
                    How do I connect my AWS Partner Central account?
                  </span>
                  <ChevronDown className="w-5 h-5 text-pb-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="text-pb-gray-600 py-3">
                  During setup, you'll deploy a CloudFormation stack that creates an IAM role
                  in your AWS account. This role grants PartnerBuddy read-only access to your
                  ACE opportunities through cross-account access.
                </p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none py-3 border-b border-pb-gray-100">
                  <span className="font-medium text-pb-gray-700">
                    Which channels can PartnerBuddy access?
                  </span>
                  <ChevronDown className="w-5 h-5 text-pb-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="text-pb-gray-600 py-3">
                  PartnerBuddy only responds in channels you explicitly configure during setup.
                  This ensures your opportunity data is only visible to authorized team members.
                </p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none py-3 border-b border-pb-gray-100">
                  <span className="font-medium text-pb-gray-700">
                    Are AI responses always accurate?
                  </span>
                  <ChevronDown className="w-5 h-5 text-pb-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="text-pb-gray-600 py-3">
                  PartnerBuddy uses AI to interpret your questions and format responses.
                  While we strive for accuracy, AI-generated content may occasionally contain
                  errors. Always verify critical business information through the official
                  AWS Partner Central console.
                </p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none py-3 border-b border-pb-gray-100">
                  <span className="font-medium text-pb-gray-700">
                    How do I request deletion of my data?
                  </span>
                  <ChevronDown className="w-5 h-5 text-pb-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="text-pb-gray-600 py-3">
                  Email us at support@rhythmic.dev with your workspace ID and we'll process
                  your deletion request within 30 days. You can also uninstall the app from
                  your Slack workspace and cancel your AWS Marketplace subscription.
                </p>
              </details>

              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none py-3">
                  <span className="font-medium text-pb-gray-700">
                    How do I update my channel configuration?
                  </span>
                  <ChevronDown className="w-5 h-5 text-pb-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="text-pb-gray-600 py-3">
                  Mention @PartnerBuddy and say "setup" in any authorized channel. This will
                  provide a link to access the setup portal where you can modify your
                  configuration.
                </p>
              </details>
            </div>
          </div>

          {/* Documentation Links */}
          <div className="bg-white rounded-2xl shadow-rhythmic-xl p-6">
            <h2 className="text-lg font-semibold text-pb-gray-800 mb-4">
              Documentation & Policies
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/privacy"
                className="flex items-center p-4 border border-pb-gray-200 rounded-xl hover:border-pb-orange hover:bg-pb-orange/5 transition-all"
              >
                <FileText className="w-5 h-5 text-pb-gray-500 mr-3" />
                <span className="font-medium text-pb-gray-700">Privacy Policy</span>
              </Link>
              <Link
                to="/terms"
                className="flex items-center p-4 border border-pb-gray-200 rounded-xl hover:border-pb-orange hover:bg-pb-orange/5 transition-all"
              >
                <FileText className="w-5 h-5 text-pb-gray-500 mr-3" />
                <span className="font-medium text-pb-gray-700">Terms of Service</span>
              </Link>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="text-center mt-8 text-pb-gray-500 text-sm">
            <p>
              Can't find what you're looking for?{' '}
              <a href="mailto:support@rhythmic.dev" className="text-pb-orange hover:underline">
                Contact us directly
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
